<script setup lang="ts" generic="TData, TValue">
  import type {
    SortingState,
    ColumnFiltersState,
    VisibilityState,
    ExpandedState,
    HeaderContext,
    Row
  } from '@tanstack/vue-table'
  import {
    FlexRender,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    getExpandedRowModel,
    useVueTable
  } from '@tanstack/vue-table'
  import { useResizeObserver } from '@vueuse/core'
  import {
    computed,
    h,
    onMounted,
    ref,
    useTemplateRef,
    onUnmounted,
    reactive
  } from 'vue'
  import { type HTMLAttributes } from 'vue'
  import { valueUpdater } from '../../lib/utils'
  import { cn } from '../../lib/utils'
  import Spinner from '../spinner.vue'
  import type { DataTableActionsProps } from './DataTableActions.vue'
  import DataTableActions from './DataTableActions.vue'
  import DataTableColumnHeader from './DataTableColumnHeader.vue'
  import DataTableStatusCell from './DataTableStatusCell.vue'
  import {
    defaultColumnSizing,
    type GatColumnDef,
    type StatusColumnDef,
    getIsInactive,
    getIsUpdated,
    getIsLoading,
    type CustomStatusOptions
  } from './index'
  import { globalUtils } from '@gat-ship/utils'

  export interface DataTableProps<TData, TValue> {
    /** The key of the row ID.
     * @default 'id'
     */
    rowId?: keyof TData
    /** The columns to show in the table. Required.
     */
    columns: GatColumnDef<TData, TValue>[]
    /** The data to show in the table. Required.
     */
    data: TData[]
    /** The class to apply to the table.
     */
    class?: HTMLAttributes['class']
    /** The height of the rows in pixels.
     * @default 60
     */
    rowHeight?: number
    /** If true, the table will autofocus on scroll.
     * @default false
     */
    autofocusOnScroll?: boolean
    /** If true, the table will show pagination.
     * @default false
     */
    pagination?: boolean
    /** The actions to show in the last column.
     * @default undefined
     */
    actions?: DataTableActionsProps<TData>
    /** If true, the table will show a loading state.
     * @default false
     */
    loading?: boolean
    /** The number of rows to show in the table.
     * @default 10
     */
    rows?: number | 'screen-height'
    /** The key of the column to render as status. */
    rowStatus?: StatusColumnDef<TData>

    /**
     * Strategy for determining if a row has been updated.
     * - Function: Custom logic based on row data
     * - Array of IDs: Row IDs that have been updated
     * - Key: Property name that contains updated state
     *
     * @example
     * ```typescript
     * // Function approach
     * isUpdated: (row) => row.updatedAt > Date.now() - 60000
     *
     * // Array of IDs approach (recommended for dynamic updates)
     * isUpdated: ['company-123', 'company-456']
     *
     * // Property key approach
     * isUpdated: 'isUpdated'
     * ```
     */
    isRowUpdated?: ((row: TData) => boolean) | keyof TData
    /**
     * Strategy for determining if a row is loading.
     * - Function: Custom logic based on row data
     * - Array of IDs: Row IDs that are currently loading
     * - Key: Property name that contains loading state
     *
     * @example
     * ```typescript
     * // Function approach
     * isLoading: (row) => row.isProcessing
     *
     * // Array of IDs approach
     * isLoading: ['row-1', 'row-2', 'row-3']
     *
     * // Property key approach
     * isLoading: 'isLoading'
     * ```
     */
    isRowLoading?: ((row: TData) => boolean) | keyof TData
    /**
     * Strategy for determining if a row is inactive.
     * - Function: Custom logic based on row data
     * - Key: Property name that contains inactive state
     *
     * @example
     * ```typescript
     * // Function approach
     * isInactive: (row) => !row.isActive
     *
     * // Property key approach
     * isInactive: 'isInactive'
     * ```
     */
    isRowInactive?: ((row: TData) => boolean) | keyof TData
    customStatus?: (row: TData) => CustomStatusOptions | undefined

    /** The test id for the table. */
    testIds?: {
      /** The test id for the table. */
      table?: string
      /** The test id for the table row. */
      row?: (row: TData) => string
      /** The test id for the table cell. */
      cell?: (row: TData, columnId: string) => string
    }
  }

  const props = withDefaults(defineProps<DataTableProps<TData, TValue>>(), {
    rowHeight: 64,
    rows: 10
  })

  const tableContainer = useTemplateRef('tableContainer')

  const hasEvent = reactive({ clickRow: false })

  onMounted(() => {
    hasEvent.clickRow =
      globalUtils.functions.components.hasEmitListener('click:row')
  })

  const tableWidth = ref(0)

  function onTableContainerMounted() {
    useResizeObserver(tableContainer.value, (entries) => {
      const entry = entries[0]
      const { width, height } = entry.contentRect
      tableWidth.value = width
    })
  }

  function getLastColumnDef() {
    const headers = table.getHeaderGroups()
    return headers[headers.length - 1]!.headers[
      headers[headers.length - 1]!.headers.length - 1
    ]!.column.columnDef
  }

  function getRemainingWidth(lastColSize: number) {
    const minWidth = getLastColumnDef().minSize ?? 0
    const currentTableWidth = table.getTotalSize()
    const tableWidthWithoutLastCol = currentTableWidth - lastColSize
    const remainingWidth = tableWidth.value - tableWidthWithoutLastCol
    if (remainingWidth < minWidth) {
      return minWidth
    }
    return remainingWidth
  }

  const columnSizeVars = computed(() => {
    const headers = table.getHeaderGroups()
    const colSizes: { [key: string]: number } = {}
    for (let i = 0; i < headers.length; i++) {
      const headerGroup = headers[i]!
      for (let j = 0; j < headerGroup.headers.length; j++) {
        const header = headerGroup.headers[j]!
        const headerSize = header.getSize()
        const colSize = header.column.getSize()
        // const remainingWidth = currentTableWidth - tableWidth.value
        const isLastCol = j === headerGroup.headers.length - 1
        // console.log(remainingWidth)
        // colSizes[`--header-${header.id}-size`] = isLastCol ? remainingWidth : headerSize
        // colSizes[`--col-${header.id}-size`] = isLastCol ? remainingWidth : colSize
        colSizes[`--header-${header.id}-size`] = isLastCol
          ? getRemainingWidth(headerSize)
          : headerSize
        colSizes[`--col-${header.id}-size`] = isLastCol
          ? getRemainingWidth(colSize)
          : colSize
      }
    }
    colSizes['--row-height'] = props.rowHeight || 60
    colSizes['--table-width'] = tableWidth.value
    return colSizes
  })

  const emit = defineEmits<{
    (e: 'click:row', row: TData): void
    (e: 'hover:row', row: TData): void
    (e: 'mouseleave:row', row: TData): void
    /**
     * Emitted when a row's status should be updated (e.g., clearing 'updated' status)
     * Parent components should handle this by updating their status arrays
     * @example
     * ```vue
     * <DataTable
     *   :data="companies"
     *   :row-status="{ isUpdated: updatedCompanyIds }"
     *   @update:row-status="(rowId, status) => handleStatusUpdate(rowId, status)"
     * />
     * ```
     */
    (
      e: 'update:row-status',
      payload: { isLoading: string[]; isUpdated: string[] }
    ): void
  }>()

  const sorting = ref<SortingState>([])
  const columnFilters = ref<ColumnFiltersState>([])
  const columnVisibility = ref<VisibilityState>({})
  const rowSelection = ref({})
  const expanded = ref<ExpandedState>({})

  const loadingRowIds = defineModel<string[] | Set<string>>('loadingRowIds', {
    required: false,
    default: () => []
  })
  const updatedRowIds = defineModel<string[] | Set<string>>('updatedRowIds', {
    required: false,
    default: () => []
  })

  const table = useVueTable({
    getRowId: (row) => {
      if (props.rowId) {
        return (row as any)[props.rowId]
      }
      if (
        typeof row === 'object' &&
        row !== null &&
        Object.keys(row).length > 0
      ) {
        const id = Object.values(row)[0]
        if (typeof id !== 'string' && typeof id !== 'number') {
          console.warn(
            'Row ID is not a string or number. Please set the rowId prop to the key of the row ID.',
            row
          )
        }
        return String(id)
      }
      return String((row as any).id)
    },
    get data() {
      return props.data
    },
    get columns() {
      // Default columns
      const result = [...props.columns]
      const actionsCol = result.find((c) => c.id === 'actions')
      if (!actionsCol && props.actions) {
        result.push({
          id: 'actions',
          enableResizing: false,
          size: props.actions?.size ?? 60,
          minSize: props.actions?.size ?? 60,
          maxSize: props.actions?.size ?? 60
        })
      }
      const statusCol: GatColumnDef<TData, TValue> = {
        id: 'status',
        enableResizing: false,
        size: 32,
        minSize: 32,
        maxSize: 32,
        // size: 48,
        // minSize: 48,
        // maxSize: 48,
        header: () =>
          h(
            'div',
            { class: 'flex grow' }
            /* h(DataTableStatusHeader, { table: table as Table<unknown> }) */
          ),
        cell: ({ row }) => {
          return h(DataTableStatusCell, {
            isNew: getIsUpdated(
              row.original,
              Array.from(updatedRowIds.value),
              props.isRowUpdated,
              row.id
            ),
            isLoading: getIsLoading(
              row.original,
              Array.from(loadingRowIds.value),
              props.isRowLoading,
              row.id
            )
          })
        }
      }
      // Move actions column to beginning if it exists
      if (false /* statusCol */) {
        // result.splice(result.indexOf(statusCol), 1)
        result.unshift(statusCol)
      }
      return result
    },
    defaultColumn: {
      size: defaultColumnSizing.size,
      minSize: defaultColumnSizing.minSize,
      maxSize: defaultColumnSizing.maxSize,

      header: (headerContext) =>
        headerContext.header.column.id === 'actions' && props.actions
          ? undefined
          : h(DataTableColumnHeader, {
              header: headerContext as HeaderContext<unknown, unknown>
            }),
      cell: (cellContext) =>
        cellContext.column.id === 'actions' && props.actions
          ? h(DataTableActions, {
              ...props.actions,
              row: cellContext.row.original
            } as DataTableActionsProps<unknown> & { row: TData })
          : h('div', cellContext.cell.getValue() ?? undefined)
    },
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: props.pagination
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: (updaterOrValue) => valueUpdater(updaterOrValue, sorting),
    onColumnFiltersChange: (updaterOrValue) =>
      valueUpdater(updaterOrValue, columnFilters),
    onColumnVisibilityChange: (updaterOrValue) =>
      valueUpdater(updaterOrValue, columnVisibility),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: (updaterOrValue) =>
      valueUpdater(updaterOrValue, rowSelection),
    onExpandedChange: (updaterOrValue) =>
      valueUpdater(updaterOrValue, expanded),
    state: {
      get sorting() {
        return sorting.value
      },
      get columnFilters() {
        return columnFilters.value
      },
      get columnVisibility() {
        return columnVisibility.value
      },
      get rowSelection() {
        return rowSelection.value
      },
      get expanded() {
        return expanded.value
      }
    }
  })

  const tableId = ref(
    `flex-data-table-${Math.random().toString(36).substring(2, 15)}`
  )

  const headerHeight = 32

  function onTableContainerScroll(event: Event) {
    if (props.autofocusOnScroll) {
      const element = document.getElementById(tableId.value)
      if (element) {
        const rect = element.getBoundingClientRect()
        window.scrollTo({
          top: window.scrollY + rect.top - 64, // 64 is the height of the header
          behavior: 'smooth'
        })
      }
    }
  }

  function getMinHeight() {
    const visibleRows = table.getRowModel().rows.length
    if (visibleRows === 0) {
      // There are no rows, so we need to show the default height.
      return `fit-content`
    }
    if (props.rows === 'screen-height') {
      return `calc(100svh - 68px)`
    }
    if (typeof props.rows === 'number' && visibleRows >= props.rows) {
      return `calc(${props.rowHeight}px * ${props.rows} + ${headerHeight}px + 4px)`
    }
    return `calc(${props.rowHeight}px * ${visibleRows} + ${headerHeight}px + 4px)`
  }

  function getMaxHeight() {
    if (props.rows === 'screen-height') {
      return `calc(100svh - 68px)`
    }
    return `calc(${props.rowHeight}px * ${props.rows} + ${headerHeight}px + ${props.rows + 1}px)`
  }

  function onMouseOverRow(row: Row<TData>) {
    emit('hover:row', row.original)
  }

  function getRowKey(row: Row<TData>) {
    return row.id
  }

  // Track hover timers for each row
  const hoverTimers = ref<Map<string, number>>(new Map())

  // Cleanup timers on unmount
  onUnmounted(() => {
    hoverTimers.value.forEach((timer) => clearTimeout(timer))
    hoverTimers.value.clear()
  })

  function onMouseEnterRow(row: Row<TData>) {
    // Clear any existing timer for this row
    const existingTimer = hoverTimers.value.get(row.id)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // Start a new timer for fade effect (400ms)
    if (
      getIsUpdated(
        row.original,
        Array.from(updatedRowIds.value),
        props.isRowUpdated,
        row.id
      )
    ) {
      const timer = setTimeout(() => {
        // Emit event to parent to update the row status
        updatedRowIds.value = new Set(
          Array.from(updatedRowIds.value).filter((id) => id !== row.id)
        )
        hoverTimers.value.delete(row.id)
      }, 400) // 400ms hover time

      hoverTimers.value.set(row.id, timer)
    }

    emit('hover:row', row.original)
  }

  function onMouseLeaveRow(row: Row<TData>) {
    // Clear the timer when mouse leaves
    const timer = hoverTimers.value.get(row.id)
    if (timer) {
      clearTimeout(timer)
      hoverTimers.value.delete(row.id)
    }

    emit('mouseleave:row', row.original)
  }

  function onRowClick(row: Row<TData>) {
    // If the row has 'updated' status, emit event to clear it
    if (
      getIsUpdated(
        row.original,
        Array.from(updatedRowIds.value),
        props.isRowUpdated,
        row.id
      )
    ) {
      updatedRowIds.value = new Set(
        Array.from(updatedRowIds.value).filter((id) => id !== row.id)
      )
    }

    // Emit the click event for normal handling
    emit('click:row', row.original)
  }

  function getCellTestId(data: TData, columnId: string) {
    if (props.testIds?.cell) {
      return props.testIds.cell(data, columnId)
    }
    return undefined
  }

  function getCustomStatus(row: Row<TData>) {
    if (props.rowStatus?.customStatus) {
      return props.rowStatus.customStatus(row.original)
    }
    return undefined
  }

  function getCustomStatusClass(row: Row<TData>) {
    const customStatus = getCustomStatus(row)
    const result = []
    if (customStatus?.backgroundColor) {
      result.push(customStatus.backgroundColor)
    }
    if (customStatus?.borderColor) {
      result.push(customStatus.borderColor)
    }
    if (customStatus?.hoverBackgroundColor) {
      result.push(customStatus.hoverBackgroundColor)
    }
    return result.join(' ')
  }

  defineExpose({
    table
  })
</script>

<template>
  <div
    :id="tableId"
    ref="tableContainer"
    :class="cn('relative overflow-x-auto', props.class)"
    :style="{
      minHeight: getMinHeight(),
      maxHeight: getMaxHeight()
    }"
    :data-testid="props.testIds?.table ?? $attrs.dataTestid"
    @scroll="onTableContainerScroll"
    @vue:mounted="onTableContainerMounted"
  >
    <div class="w-fit" :style="{ ...columnSizeVars }">
      <div
        v-for="headerGroup in table.getHeaderGroups()"
        :key="headerGroup.id"
        class="bg-secondary sticky top-0 z-10 flex rounded-sm"
      >
        <div
          v-for="header in headerGroup.headers"
          :key="header.id"
          :class="
            cn(
              'text-soft relative flex flex-col items-start justify-end px-3',
              header.column.id === 'status' && 'items-center px-0'
            )
          "
          :style="{
            width: `calc(var(--header-${header?.id}-size) * 1px)`
          }"
        >
          <FlexRender
            v-if="!header.isPlaceholder"
            :render="header.column.columnDef.header"
            :props="header.getContext()"
          />
          <div
            v-if="header.column.getCanResize()"
            class="resizer right-2"
            :class="{ isResizing: header.column.getIsResizing() }"
            @dblclick="header.column.resetSize()"
            @mousedown="header.getResizeHandler()?.($event)"
            @touchstart="header.getResizeHandler()?.($event)"
          ></div>
        </div>
      </div>
      <div
        class="tbody mt-1.5 space-y-1"
        :class="{ 'cursor-pointer select-none': hasEvent.clickRow }"
      >
        <template v-if="!loading && table.getRowModel().rows?.length">
          <template
            v-for="(row, index) in table.getRowModel().rows"
            :key="getRowKey(row)"
          >
            <!-- 
            Removed 27.06-2025: Add these back to see what was removed.
            data-[new=true]:before:absolute data-[new=true]:before:left-0 data-[new=true]:before:top-0 data-[new=true]:before:h-full data-[new=true]:before:w-1 data-[new=true]:before:bg-amber-500
            -->
            <div
              :class="[
                cn(
                  'hover:bg-accent data-[state=selected]:bg-accent data-[loading=true]:text-soft/60 group/row relative flex w-fit rounded-sm text-sm ease-in-out data-[loading=true]:animate-pulse data-[new=true]:bg-amber-50',
                  getCustomStatusClass(row)
                )
              ]"
              :data-state="row.getIsSelected() ? 'selected' : undefined"
              :data-loading="
                getIsLoading(
                  row.original,
                  Array.from(loadingRowIds),
                  props.isRowLoading,
                  row.id
                )
              "
              :data-new="
                getIsUpdated(
                  row.original,
                  Array.from(updatedRowIds),
                  props.isRowUpdated,
                  row.id
                )
              "
              :data-inactive="getIsInactive(row.original, props.isRowInactive)"
              :data-testid="
                props.testIds?.row?.(row.original) ?? $attrs.dataTestid
              "
              :data-custom-status="getCustomStatus(row) !== undefined"
              @click="onRowClick(row)"
              @mouseenter="onMouseEnterRow(row)"
              @mouseleave="onMouseLeaveRow(row)"
            >
              <div
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :style="{
                  width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                  height: `calc(var(--row-height) * 1px)`
                }"
                :data-inactive="
                  getIsInactive(row.original, props.isRowInactive)
                "
                :data-testid="
                  getCellTestId(row.original, cell.column.id) ??
                  $attrs.dataTestid
                "
                :class="[
                  cn(
                    'flex grow flex-col justify-center overflow-hidden text-ellipsis px-3'
                  ),
                  {
                    'items-center px-0': cell.column.id === 'status',
                    'data-[inactive=true]:opacity-60 data-[inactive=true]:before:absolute data-[inactive=true]:before:left-0 data-[inactive=true]:before:top-0 data-[inactive=true]:before:h-full data-[inactive=true]:before:w-1 data-[inactive=true]:before:bg-gray-300 data-[inactive=true]:hover:opacity-100':
                      cell.column.id !== 'status' &&
                      cell.column.id !== 'actions'
                  }
                ]"
              >
                <slot name="cell" v-bind="{ cell, column: cell.column }">
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="{
                      ...cell.getContext(),
                      class: cn('overflow-hidden truncate')
                    }"
                  />
                </slot>
              </div>
            </div>
            <div v-if="index !== table.getRowModel().rows.length - 1">
              <div
                role="separator"
                class="before:bg-border relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
              ></div>
            </div>
          </template>
        </template>
        <template v-else>
          <div class="transition-color flex w-full border-b text-sm">
            <div
              v-if="loading"
              :col-span="columns.length"
              class="grow-1 flex h-24 flex-col items-center justify-center"
            >
              <div class="flex flex-col items-center justify-center">
                <Spinner class="text-soft size-8" />
                Loading...
              </div>
            </div>
            <div
              v-else
              class="hover:bg-accent flex h-24 w-full grow items-center justify-center text-center"
            >
              No results.
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<style scoped>
  * {
    box-sizing: border-box;
  }

  /* table,
.divTable {
  width: fit-content;
} */

  th,
  .th {
    position: relative;
  }

  .resizer {
    position: absolute;
    top: 0;
    height: 100%;
    right: 5px;
    width: 5px;
    background: rgba(0, 0, 0, 0.5);
    cursor: col-resize;
    user-select: none;
    touch-action: none;
  }

  .resizer.isResizing {
    background: blue;
    opacity: 1;
  }

  @media (hover: hover) {
    .resizer {
      opacity: 0;
    }

    *:hover > .resizer {
      opacity: 1;
    }
  }
</style>
